import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { EMICalculatorImage, EMIRound } from "../assets/Images";

const EMICalculatorModal = ({ onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(6);
  const [loanTenure, setLoanTenure] = useState(12);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500); // should match your animation duration
  };

  useEffect(() => {
    const P = loanAmount;
    const R = interestRate / 12 / 100; // ✅ monthly rate as a decimal (e.g., 0.0067)
    const N = loanTenure;

    if (P > 0 && R > 0 && N > 0) {
      const emiCalc = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
      const total = emiCalc * N;
      setEmi(parseFloat(emiCalc.toFixed(2)));
      setTotalAmount(parseFloat(total.toFixed(2)));
      setTotalInterest(parseFloat((total - P).toFixed(2)));
    }
  }, [loanAmount, interestRate, loanTenure]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const tenureOptions = [6, 9, 12, 18, 24, 30, 36, 42, 48, 54, 64];
  const tabs = ["Bike loan", "Car loan", "Home loan", "Personal loan"];
  const [activeTab, setActiveTab] = useState("Bike loan");
  const handleTabChange = (tab) => {
    setActiveTab(tab);

    switch (tab) {
      case "Bike loan":
        setLoanAmount(100000);
        setInterestRate(6);
        setLoanTenure(12);
        break;
      case "Car loan":
        setLoanAmount(500000);
        setInterestRate(7.5);
        setLoanTenure(36);
        break;
      case "Home loan":
        setLoanAmount(2500000);
        setInterestRate(8.5);
        setLoanTenure(240); // 20 years
        break;
      case "Personal loan":
        setLoanAmount(200000);
        setInterestRate(12);
        setLoanTenure(24);
        break;
      default:
        break;
    }
  };
const principalRatio = totalAmount > 0 ? loanAmount / totalAmount : 0;
const interestRatio = totalAmount > 0 ? totalInterest / totalAmount : 0;


  // Calculate angles for semi-circle (180 degrees total)
  //   const principalAngle = principalRatio * 180
  //   const interestAngle = interestRatio * 180
  return (
    <div className="fixed top-[80px] md:top-[95px] inset-0 z-50 flex justify-end bg-black bg-opacity-0 animate-fade-in">
      {/* Slide panel */}
      <div
        className={`w-full max-w-md bg-gradient-to-b rounded-l-md from-[#d6f0ff] via-[#f0faff] to-white h-full transform transition-transform duration-500 shadow-xl relative ${
          isClosing ? "animate-slide-out-right" : "animate-slide-in-right"
        }`}
      >
        {/* Close icon */}
        <button
          onClick={handleClose}
          className="top-4 right-4 text-gray-600 hover:text-red-600"
        >
          <X size={24} />
        </button>

        <div className="p-6 overflow-y-auto h-full">
          <h2 className="text-2xl font-bold mb-6">EMI Calculator</h2>

          {/* Tabs */}
          <div className="flex justify-between items-center border-b mb-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`pb-2 border-b-2 text-sm font-medium ${
                  activeTab === tab
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Loan Amount */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-gray-700">Loan amount</label>
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-[100px] px-3 py-1 text-blue-700 font-semibold border-2 border-blue-500 rounded-lg text-sm text-center"
              />
            </div>
            <input
              type="range"
              min={10000}
              max={1000000}
              step={10000}
              value={loanAmount}
              onChange={(e) => setLoanAmount(Number(e.target.value))}
              className="w-full accent-blue-700"
            />
          </div>

          {/* Interest Rate */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-gray-700">Interest Rate</label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-[100px] px-3 py-1 text-blue-700 font-semibold border-2 border-blue-500 rounded-lg text-sm text-center"
              />
            </div>
            <input
              type="range"
              min={1}
              max={20}
              step={0.1}
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full accent-blue-700"
            />
          </div>

          {/* Loan Tenure */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-gray-700">Loan Tenure</label>
              <input
                type="number"
                value={loanTenure}
                onChange={(e) => setLoanTenure(Number(e.target.value))}
                className="w-[100px] px-3 py-1 text-blue-700 font-semibold border-2 border-blue-500 rounded-lg text-sm text-center"
              />
            </div>
            <input
              type="range"
              min={8}
              max={64}
              step={1}
              value={loanTenure}
              onChange={(e) => setLoanTenure(Number(e.target.value))}
              className="w-full accent-blue-700"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {tenureOptions.map((val) => (
                <button
                  key={val}
                  onClick={() => setLoanTenure(val)}
                  className={`px-3 py-1 rounded text-sm border ${
                    loanTenure === val
                      ? "bg-blue-700 text-white"
                      : "text-gray-700 border-gray-300"
                  }`}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>

          {/* EMI Display */}
{loanAmount > 0 && (
  <div className="mb-6">
    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-xl p-6 text-center">
      <h3 className="text-lg font-semibold mb-2">Monthly EMI</h3>
      <div className="text-4xl font-bold mb-2">
        {formatCurrency(emi)}
      </div>
      <p className="text-sm opacity-80">
        You need to pay this amount every month
      </p>
    </div>
  </div>
)}


          {/* Summary */}

{    loanAmount > 0 &&      <div className="p-6 bg-white border rounded-xl shadow-sm mb-4">
            {/* Three Column Layout */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-1 h-4 bg-blue-600 rounded-full mr-2"></div>
                  <p className="text-gray-600 text-sm">Principal Amount</p>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(loanAmount)}
                </p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-1 h-4 bg-gray-400 rounded-full mr-2"></div>
                  <p className="text-gray-600 text-sm">Interest</p>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(totalInterest)}
                </p>
              </div>
            </div>

            {/* Semi-circular Chart */}
            <div className="flex justify-center">
              <div className="relative w-48 h-24">
                <svg
                  width="192"
                  height="96"
                  viewBox="0 0 192 96"
                  className="overflow-visible"
                >
                  {/* Background semi-circle */}
                  <path
                    d="M 16 80 A 80 80 0 0 1 176 80"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="16"
                    strokeLinecap="round"
                  />

                  {/* Principal amount arc (blue) */}
                  <path
                    d="M 16 80 A 80 80 0 0 1 176 80"
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="16"
                    strokeLinecap="round"
                    strokeDasharray={`${principalRatio * 251.2} 251.2`}
                    className="transition-all duration-700"
                  />

                  {/* Interest amount arc (gray) */}
                  <path
                    d="M 16 80 A 80 80 0 0 1 176 80"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="16"
                    strokeLinecap="round"
                    strokeDasharray={`${interestRatio * 251.2} 251.2`}
                    strokeDashoffset={`-${principalRatio * 251.2}`}
                    className="transition-all duration-700"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <div className="w-1 h-4 bg-green-500 rounded-full mr-2"></div>
                <p className="text-gray-600 text-sm">Total payment</p>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(loanAmount + totalInterest)}
              </p>
              <p className="text-xs text-gray-600">
                Principal + Interest over {loanTenure} month
              </p>
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

// Floating Button + Modal Controller
const EMICalculator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = () => setIsOpen(true);

  return (
    <>
      <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-50">
        <div
          className="relative flex items-center py-2 cursor-pointer transition-all duration-300"
          onClick={handleClick}
        >
          {/* Image Wrapper */}
          <div className="relative w-24 md:w-32">
            <img
              src={EMICalculatorImage}
              alt="EMI Calculator"
              className="w-full"
            />

            {/* Rotate only the icon inside the image (positioned over icon) */}
            <div className="absolute left-[4px] top-[16px] md:left-[6px] md:top-[22px]">
              <div className="w-7 h-7 md:w-9 md:h-9 animate-spin-slow hover:animate-spin">
                <img
                  src={EMIRound} // Place cropped icon alone if possible
                  alt="Rotating icon"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isOpen && <EMICalculatorModal onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default EMICalculator;
